import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';

import alert from '@drivesally/sally-ui/lib/libs/alert';
import fkOrId from '@drivesally/sally-fw/lib/lib/fkOrId';
import parseError from '@drivesally/sally-fw/lib/error/parseError';
import subscriptionHOC from '@drivesally/sally-fw/lib/event/hoc/subscription.hoc.event';

import createTeamApi from '../../api/createTeam.api.crm';
import listTeamsApi from '../../api/listTeams.api.crm';
import updateTeamApi from '../../api/updateTeam.api.crm';

import CrmTeamsModal from '../../components/CrmTeamsModal/CrmTeamsModal';

import showTeamsModalEvent from '../../events/showTeamsModal.event.crm';
import teamUpdatedEvent from '../../events/teamUpdated.event.crm';

const sortTeamsByName = (teams) =>
  [...teams].sort((a, b) => a.name.localeCompare(b.name));

class CrmTeamsContainer extends Component {
  static propTypes = {
    subscribe: PropTypes.func,
    users: PropTypes.array,
  };

  state = {
    creating: false,
    loading: false,
    members: [],
    newTeam: '',
    saving: false,
    search: '',
    selectedTeam: null,
    slackChannelError: '',
    slackChannelName: '',
    slackChannelSaving: false,
    teams: [],
    visible: false,
  };

  componentDidMount() {
    this.mounted = true;
    this.saveRequestId = 0;
    this.slackChannelRequestId = 0;
    this.props.subscribe(showTeamsModalEvent.subscribe(this.show));
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  show = () => {
    this.setState(
      {
        visible: true,
        creating: false,
        newTeam: '',
        search: '',
        selectedTeam: null,
        members: [],
        slackChannelError: '',
        slackChannelName: '',
      },
      this.loadTeams
    );
  };

  hide = () => {
    const {creating, loading, saving, slackChannelSaving} = this.state;
    if (creating || loading || saving || slackChannelSaving) return;
    this.setState({visible: false});
  };

  loadTeams = async () => {
    this.setState({loading: true});
    try {
      const {results} = await listTeamsApi({limit: 100, ordering: 'name'});
      if (!this.mounted) return;
      this.setState({
        loading: false,
        teams: results,
        selectedTeam: results[0] || null,
        members: this.getMemberIds(results[0]),
        slackChannelName: this.getChannelName(results[0]),
        slackChannelError: '',
      });
    } catch (error) {
      if (!this.mounted) return;
      alert.info(`Failed to fetch CRM teams: ${error}`);
      this.setState({loading: false, teams: []});
    }
  };

  getMemberIds = (team) =>
    [...(team?.members || [])].map((member) => fkOrId(member));

  getChannelName = (team) => team?.slack_channel?.name || '';

  getUserById = (userId) =>
    [...this.props.users].find(({id}) => id === userId);

  onTeamSelect = (team) => () => {
    this.setState({
      selectedTeam: team,
      members: this.getMemberIds(team),
      search: '',
      slackChannelName: this.getChannelName(team),
      slackChannelError: '',
    });
  };

  onNewTeamChange = (newTeam) => {
    this.setState({newTeam});
  };

  createTeam = async () => {
    const {creating, newTeam} = this.state;
    const name = newTeam.trim();
    if (creating || !name.length) return;

    this.setState({creating: true});
    try {
      const response = await createTeamApi({name});
      if (!this.mounted) return;
      this.setState({
        creating: false,
        newTeam: '',
        teams: sortTeamsByName([...this.state.teams, response]),
        selectedTeam: response,
        members: this.getMemberIds(response),
        search: '',
        slackChannelName: this.getChannelName(response),
        slackChannelError: '',
      });
      teamUpdatedEvent.publish(response);
    } catch (error) {
      const {message} = parseError(error);
      alert.error(message);
      if (!this.mounted) return;
      this.setState({creating: false});
    }
  };

  onSearch = (search) => {
    this.setState({search});
  };

  onSlackChannelChange = (slackChannelName) => {
    this.setState({slackChannelName, slackChannelError: ''});
  };

  isSlackChannelDirty = () => {
    const {selectedTeam, slackChannelName} = this.state;
    return slackChannelName.trim() !== this.getChannelName(selectedTeam);
  };

  applySlackChannel = async () => {
    const {selectedTeam, slackChannelName, slackChannelSaving} = this.state;
    if (slackChannelSaving || !selectedTeam?.id || !this.isSlackChannelDirty())
      return;

    const requestId = ++this.slackChannelRequestId;
    this.setState({slackChannelSaving: true, slackChannelError: ''});
    try {
      const response = await updateTeamApi(selectedTeam.id, {
        slack_channel_name: slackChannelName.trim(),
      });
      if (!this.mounted || requestId !== this.slackChannelRequestId) return;
      this.setState({
        slackChannelSaving: false,
        teams: this.state.teams.map((team) =>
          team.id === response.id ? response : team
        ),
        selectedTeam: response,
        slackChannelName: this.getChannelName(response),
      });
      teamUpdatedEvent.publish(response);
    } catch (error) {
      if (!this.mounted || requestId !== this.slackChannelRequestId) return;
      const {message} = parseError(error);
      this.setState({slackChannelSaving: false, slackChannelError: message});
    }
  };

  onMemberAdd = (userId) => () => {
    const {members, saving} = this.state;
    if (saving || members.includes(userId)) return;
    this.setState({members: [...members, userId]}, this.persistTeam);
  };

  onMemberRemove = (userId) => () => {
    const {members, saving} = this.state;
    if (saving) return;
    this.setState(
      {members: members.filter((id) => id !== userId)},
      this.persistTeam
    );
  };

  persistTeam = async () => {
    const {members, saving, selectedTeam} = this.state;
    if (saving || !selectedTeam?.id) return;

    const requestId = ++this.saveRequestId;
    this.setState({saving: true});
    try {
      const response = await updateTeamApi(selectedTeam.id, {members});
      if (!this.mounted || requestId !== this.saveRequestId) return;
      this.setState({
        saving: false,
        teams: this.state.teams.map((team) =>
          team.id === response.id ? response : team
        ),
        selectedTeam: response,
        members: this.getMemberIds(response),
      });
      teamUpdatedEvent.publish(response);
    } catch (error) {
      if (!this.mounted || requestId !== this.saveRequestId) return;
      const {message} = parseError(error);
      alert.error(message);
      this.setState({saving: false});
    }
  };

  assignedUsers = () => {
    const {members} = this.state;
    return members
      .map((userId) => this.getUserById(userId))
      .filter(Boolean)
      .sort((a, b) => (a.username || '').localeCompare(b.username || ''));
  };

  availableUsers = () => {
    const {users} = this.props;
    const {members, search} = this.state;
    const formattedSearch = search.trim().toLowerCase();
    if (!formattedSearch.length) return [];

    return [...users]
      .filter((user) => !members.includes(user.id))
      .filter((user) => {
        const name = [user.first_name, user.last_name]
          .filter(Boolean)
          .join('');
        return [name, user.email || '', user.username || ''].some(
          (searchValue) =>
            !!searchValue &&
            searchValue.toLowerCase().includes(formattedSearch)
        );
      })
      .sort((a, b) => (a.username || '').localeCompare(b.username || ''));
  };

  render() {
    const {
      creating,
      loading,
      newTeam,
      saving,
      search,
      selectedTeam,
      slackChannelError,
      slackChannelName,
      slackChannelSaving,
      teams,
      visible,
    } = this.state;

    return (
      <CrmTeamsModal
        assignedUsers={this.assignedUsers()}
        availableUsers={this.availableUsers()}
        creating={creating}
        loading={loading}
        newTeam={newTeam}
        onClose={this.hide}
        onCreateTeam={this.createTeam}
        onMemberAdd={this.onMemberAdd}
        onMemberRemove={this.onMemberRemove}
        onNewTeamChange={this.onNewTeamChange}
        onSearch={this.onSearch}
        onSlackChannelApply={this.applySlackChannel}
        onSlackChannelChange={this.onSlackChannelChange}
        onTeamSelect={this.onTeamSelect}
        saving={saving}
        search={search}
        selectedTeam={selectedTeam}
        slackChannelDirty={this.isSlackChannelDirty()}
        slackChannelError={slackChannelError}
        slackChannelName={slackChannelName}
        slackChannelSaving={slackChannelSaving}
        teams={teams}
        visible={visible}
      />
    );
  }
}

export default connect((state) => ({
  users: state.user.users,
}))(subscriptionHOC(CrmTeamsContainer));
